/**
 * Session helpers.
 *
 * Common things to do with getting cookie information from session. Because the app
 * is rendered on the server cookies are used to pass things (token) from server to client.
 */

import cookie from 'js-cookie';
import { serialize } from 'cookie';

/**
 * Sets a cookie on the server.
 *
 * @param {string} key
 *  The key for the cookie to be stored.
 *
 * @param {string} value
 *  The value for the cookie to be stored.
 *
 * @param {object} res
 *  The response object where to cookie will be set.
 */
export const setServerCookie = (key, value, res) => {
  if (!process.browser) {
    // Cookies with the `__Secure-` / `__Host-` prefix MUST be sent with the
    // Secure attribute, otherwise browsers silently reject them.
    const requiresSecurePrefix =
      key.startsWith('__Secure-') || key.startsWith('__Host-');
    res.setHeader(
      'Set-Cookie',
      serialize(key, value, {
        secure: requiresSecurePrefix,
        path: '/',
        httpOnly: true,
        sameSite: 'Lax',
      }),
    );
  }
};

/**
 * Get a cookie by its key.
 *
 * Will check if the current process is browser or node (server rendering) and retrieve from browser
 * or server accordingly.
 *
 * @param {string} key
 *  The key for the cookie to be retrieved.
 *
 * @param {string} req
 *  The request object send with node.
 */
export const getCookie = (key, req) => {
  return process.browser
    ? getCookieFromBrowser(key)
    : getCookieFromServer(key, req);
};

/**
 * Get a cookie using js-cookie (browser).
 *
 * @param {string} key
 *  The key for the cookie to be retrieved.
 */
export const getCookieFromBrowser = (key) => {
  return cookie.get(key);
};

/**
 * Sets the browser cookie using js-cookie.
 *
 * @param {string} key
 *  The cookie key.
 * @param value
 *  The cookie value.
 * @param settings
 *  The cookie settings.
 */
export function setBrowserCookie(
  key: string,
  value: string,
  settings: Object,
): void {
  cookie.set(key, value, settings);
}

/**
 * Get a cookie using the nodejs request object.
 *
 * @param {string} key
 *  The key for the cookie to be retrieved.
 *
 * @param {string} req
 *  The request object send with node.
 */
const getCookieFromServer = (key, req) => {
  if (!req || !req.headers.cookie) {
    return undefined;
  }
  const rawCookie = req.headers.cookie
    .split(';')
    .find((c) => c.trim().startsWith(`${key}=`));
  if (!rawCookie) {
    return undefined;
  }
  return rawCookie.split('=')[1];
};

/**
 * Generates/gets a token to be used for anonymous job posting.
 *
 * @return {string}
 *  Returns the token.
 */
export const getAnonymousUserToken = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  let token = localStorage.getItem('userToken');
  const urlParams = new URLSearchParams(window.location.search);
  const anonymousTokenFromUrl = urlParams.get('anonymous_user_token');
  if (anonymousTokenFromUrl) {
    token = anonymousTokenFromUrl;
    localStorage.setItem('userToken', anonymousTokenFromUrl);
  }
  if (token === null) {
    token = Date.now().toString(36) + Math.random().toString(36);
    localStorage.setItem('userToken', token);
  }
  return token;
};
